import { describeApiError, type TournamentListItem } from '@brush/api-client';
import type { PointsScope } from '@brush/ui';
import type { UseQueryResult } from '@tanstack/react-query';
import { candidateParams, type CandidateResult } from './candidates';

/** What the scope filters' tick list draws from its read (`LeaderboardScopeFilters`). */
export interface CandidateList {
  /** Every page read, or as many as could be (`complete`). Undefined until the first answer. */
  result: CandidateResult<TournamentListItem> | undefined;
  /** No answer yet: the first read is on its way. */
  loading: boolean;
  /**
   * An answer is on screen and a newer one is due — the debounce has not yet passed the edit on, or the read
   * for it is out. Narrowing edits already show (the component applies the live scope to the rows it has); a
   * widening one waits for this.
   */
  updating: boolean;
  /** The read failed, worded for the operator. */
  failure: string | null;
  retry: () => void;
}

/**
 * The candidate read (`useScopeCandidates(roomId, candidateParams(debounced))`, called by each scope section
 * itself, so the wiring sweep sees it) shaped for the tick list. `live` is the scope on screen, `debounced` the
 * one the read follows: both sections already debounce their scope for the server's event count, and the list
 * follows the same value so the two move together. Ticking a row changes neither the params nor the key.
 */
export function candidateList(
  query: UseQueryResult<CandidateResult<TournamentListItem>, unknown>,
  live: PointsScope,
  debounced: PointsScope,
): CandidateList {
  const pending = JSON.stringify(candidateParams(live)) !== JSON.stringify(candidateParams(debounced));

  return {
    result: query.data,
    loading: query.data === undefined && query.isFetching,
    updating: query.data !== undefined && (pending || query.isFetching || query.isPlaceholderData),
    failure: query.isError
      ? (describeApiError(query.error) ?? 'This room’s tournaments could not be read.')
      : null,
    retry: () => void query.refetch(),
  };
}
